import './App.css';
import {
  Routes,
  Route,
  BrowserRouter as Router,
  Navigate,
} from 'react-router-dom';
import Signup from './pages/signup';
import Dashboard from './pages/dashboard';
import * as Sentry from '@sentry/react';
import Tracker from '@openreplay/tracker';
import RoleSelectPage from './pages/roleselect';
import Login from './pages/login';
import { ScreenViewProvider } from './utils/ScreenViewContext';
import { QueryClientProvider } from '@tanstack/react-query';
import { createAppQueryClient } from './utils/appQueryClient';
import React from 'react';
import DriverSignup from './pages/driver-signup';
import PartnerAgentSignup from './pages/partner-agent-signup';
import Error404 from './pages/404';
import DemoDashboard from './pages/demo-dashboard';
import {
  AppProviders,
  ProtectedProviders,
  DemoProviders,
} from './providers/AppProviders';
import ErrorFallback from './components/ErrorBoundary';

export const tracker = new Tracker({
  projectKey: process.env.REACT_APP_OPEN_REPLAY_PROJECT_KEY ?? '',
  ingestPoint: process.env.REACT_APP_OPEN_REPLAY_INGEST_POINT,
  // Never record input values into session replays (bank account/routing
  // numbers, EIN, etc.). 2 = InputMode.Hidden in @openreplay/tracker; the
  // obscureInput* flags only apply in Plain mode, so Hidden alone suffices.
  defaultInputMode: 2,
});

// Sentry is initialized once in src/utils/Instrument.tsx (imported first via
// index.tsx) with explicit replay masking — do not add a second init here.
const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);
// Global QueryCache/MutationCache onError → Sentry lives in appQueryClient.
const queryClient = createAppQueryClient();
function App() {
  React.useEffect(() => {
    tracker.start().then(() => {
      const sessionId = tracker.getSessionID();
      if (sessionId) Sentry.setTag('openReplaySession.id', sessionId);
      const sessionURL = tracker.getSessionURL();
      if (sessionURL) Sentry.setTag('openReplaySessionURL', sessionURL);
    });
  }, []);
  return (
    <Router>
      {/*
        Root error boundary. Sits inside Router but outside the route providers
        so a render crash anywhere in the tree (including inside a provider)
        shows the fallback instead of a blank screen. Sentry receives the
        report automatically via this boundary.
      */}
      <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
        <QueryClientProvider client={queryClient}>
          <SentryRoutes>
            {/* Public routes */}
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route
              path="/role"
              element={
                <AppProviders>
                  <RoleSelectPage />
                </AppProviders>
              }
            />
            <Route
              path="/driver-signup"
              element={
                <AppProviders>
                  <DriverSignup />
                </AppProviders>
              }
            />
            <Route
              path="/partner-agent-signup"
              element={
                <AppProviders>
                  <PartnerAgentSignup />
                </AppProviders>
              }
            />
            <Route
              path="/login"
              element={
                <AppProviders>
                  <Login />
                </AppProviders>
              }
            />
            {/* Protected routes */}
            <Route
              path="/dashboard/*"
              element={
                <ProtectedProviders>
                  <Dashboard />
                </ProtectedProviders>
              }
            />
            <Route
              path="/signup"
              element={
                <ProtectedProviders>
                  <Signup />
                </ProtectedProviders>
              }
            />
            {/* Demo route - no auth required */}
            <Route
              path="/demo/*"
              element={
                <DemoProviders>
                  <DemoDashboard />
                </DemoProviders>
              }
            />
            <Route
              path="/404"
              element={
                <ScreenViewProvider>
                  <Error404 />
                </ScreenViewProvider>
              }
            />
            {/* Catch-all: any unknown URL lands on the 404 page (mirrors the
                /404 wrapping) instead of rendering a blank screen. Must stay
                last. */}
            <Route
              path="*"
              element={
                <ScreenViewProvider>
                  <Error404 />
                </ScreenViewProvider>
              }
            />
          </SentryRoutes>
        </QueryClientProvider>
      </Sentry.ErrorBoundary>
    </Router>
  );
}

export default App;
